import { HTTPService } from './HttpService';
import { Photo, PhotoShareLink } from '../types';
import { createLogger } from '../utils/logger';

const logger = createLogger('[PhotoService]');

export interface UploadProgress {
  fileIndex: number;
  fileName: string;
  status: 'pending' | 'uploading' | 'done' | 'error' | 'cancelled';
  progress: number; // 0-100
  error?: string;
  retries?: number;
}

export interface UploadCallbacks {
  onProgress?: (state: UploadProgress[]) => void;
  onError?: (error: Error) => void;
  onComplete?: (photos: Photo[]) => void;
}

export class PhotoService {
  private httpService: HTTPService;
  private concurrency: number;
  private maxRetries: number;

  constructor(httpService: HTTPService) {
    this.httpService = httpService;
    this.concurrency = parseInt(import.meta.env.VITE_UPLOAD_CONCURRENCY || '1', 10);
    this.maxRetries = parseInt(import.meta.env.VITE_UPLOAD_MAX_RETRIES || '3', 10);
  }

  async getPhotos(
    limit: number = 20,
    offset: number = 0,
    filters?: { tag?: number; tags?: string; visibility?: 'public' | 'private' | 'all' }
  ): Promise<Photo[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    // Multi-tag filtering (preferred)
    if (filters?.tags) {
      params.append('tags', filters.tags);
    }
    // Single tag filtering (backwards compatibility)
    else if (filters?.tag) {
      params.append('tag', filters.tag.toString());
    }
    
    if (filters?.visibility) {
      params.append('visibility', filters.visibility);
    }

    const response = await this.httpService.get(`/photos?${params.toString()}`);
    return response.data;
  }

  async getPhotoById(id: number): Promise<Photo> {
    const response = await this.httpService.get(`/photos/${id}`);
    return response.data;
  }

  /**
    * Upload photos directly to S3 with metadata extraction
    * Handles multipart file upload, thumbnail generation, and EXIF extraction
    * Optionally applies tags to all uploaded photos
    * @param files - Array of File objects from input element
    * @param tagIds - Optional array of tag IDs to apply to all uploaded photos
    * @returns Array of created Photo objects with S3 keys and metadata
    */
  async uploadPhotos(files: File[], tagIds?: number[]): Promise<Photo[]> {
    const formData = new FormData();
    
    // Append all files to the FormData object
    for (const file of files) {
      formData.append('files', file);
    }

    // Append tagIds as JSON string if provided
    if (tagIds && tagIds.length > 0) {
      formData.append('tagIds', JSON.stringify(tagIds));
    }

    // POST to the new upload endpoint with multipart/form-data
    const response = await this.httpService.post('/photos/upload', formData, {
      headers: {
        // Don't set Content-Type - let the browser set it with the boundary
        'Content-Type': undefined,
      },
    });
    
    return response.data;
  }

  async updatePhotoMetadata(
    photoId: number,
    updates: { taken_at?: string; location?: string; description?: string }
  ): Promise<Photo> {
    const response = await this.httpService.patch(`/photos/${photoId}`, updates);
    return response.data;
  }

  async updateVisibility(
    photoId: number,
    visibility: 'public' | 'private',
    viewers?: number[]
  ): Promise<Photo> {
    const response = await this.httpService.patch(`/photos/${photoId}/visibility`, {
      visibility,
      viewers: viewers || [],
    });
    return response.data;
  }

  async deletePhoto(photoId: number): Promise<void> {
    await this.httpService.delete(`/photos/${photoId}`);
  }

  async getShareLink(photoId: number): Promise<PhotoShareLink | null> {
    const response = await this.httpService.get(`/photos/${photoId}/share-link`);
    return response.data;
  }

  async createShareLink(photoId: number): Promise<PhotoShareLink> {
    const response = await this.httpService.post(`/photos/${photoId}/share-link`, {});
    return response.data;
  }

  async revokeShareLink(photoId: number): Promise<void> {
    await this.httpService.delete(`/photos/${photoId}/share-link`);
  }

  /**
   * Upload photos with per-file progress tracking, concurrency control, and cancellation support
   * @param files - Array of File objects to upload
   * @param tagIds - Optional array of tag IDs to apply to all photos
   * @param callbacks - Callbacks for progress, error, and completion events
   * @returns Promise<Photo[]> - Array of successfully uploaded photos
   */
  async uploadPhotosWithProgress(
    files: File[],
    tagIds?: number[],
    callbacks?: UploadCallbacks,
    abortSignal?: AbortSignal
  ): Promise<Photo[]> {
    logger.debug(`Starting upload for ${files.length} files with concurrency=${this.concurrency}`);

    // Initialize progress state for all files
    const progressState: UploadProgress[] = files.map((file, index) => ({
      fileIndex: index,
      fileName: file.name,
      status: 'pending',
      progress: 0,
      retries: 0,
    }));

     const uploadedPhotos: Photo[] = [];
     const queue: number[] = Array.from({ length: files.length }, (_, i) => i); // File indices to process
     let activeUploads = 0;

     // Helper to create immutable state update (ensures React detects changes)
     const updateFileState = (fileIndex: number, patch: Partial<UploadProgress>) => {
       progressState[fileIndex] = { ...progressState[fileIndex], ...patch };
       // Create new array reference so React re-renders (not just mutating same reference)
       callbacks?.onProgress?.([...progressState]);
     };

    const uploadFile = async (fileIndex: number): Promise<Photo | null> => {
      const file = files[fileIndex];
      const state = progressState[fileIndex];
      let attempt = 0;

      while (attempt < this.maxRetries) {
        try {
          // Check if upload was cancelled
          if (abortSignal?.aborted) {
            updateFileState(fileIndex, { status: 'cancelled', progress: 0 });
            logger.info(`Upload cancelled for file: ${file.name}`);
            return null;
          }

          updateFileState(fileIndex, { status: 'uploading', retries: attempt, progress: 0 });

          // Create FormData with single file
          const formData = new FormData();
          formData.append('files', file);

          // Append tagIds if provided
          if (tagIds && tagIds.length > 0) {
            formData.append('tagIds', JSON.stringify(tagIds));
          }

          // Create abort controller for this specific request
          const fileAbortController = new AbortController();
          const abortListener = () => fileAbortController.abort();

          if (abortSignal) {
            abortSignal.addEventListener('abort', abortListener);
          }

          try {
            // Upload with progress tracking
            const response = await this.httpService.post('/photos/upload', formData, {
              headers: {
                'Content-Type': undefined,
              },
              onUploadProgress: (progressEvent: ProgressEvent) => {
                if (progressEvent.lengthComputable) {
                  updateFileState(fileIndex, {
                    progress: Math.round((progressEvent.loaded / progressEvent.total) * 100),
                  });
                }
              },
              signal: fileAbortController.signal,
            });

            // Mark as complete
            updateFileState(fileIndex, { status: 'done', progress: 100 });

            const uploadedPhoto = response.data[0]; // Backend returns array with single photo
            logger.info(`Successfully uploaded: ${file.name}`);
            return uploadedPhoto;
          } finally {
            if (abortSignal) {
              abortSignal.removeEventListener('abort', abortListener);
            }
          }
        } catch (err) {
          attempt++;
          const errorMsg = err instanceof Error ? err.message : 'Unknown error';

          if (abortSignal?.aborted) {
            state.status = 'cancelled';
            state.progress = 0;
            logger.info(`Upload cancelled for file: ${file.name}`);
            return null;
          }

          if (attempt >= this.maxRetries) {
            updateFileState(fileIndex, { status: 'error', error: errorMsg, progress: 0 });
            logger.error(`Failed to upload ${file.name} after ${this.maxRetries} attempts:`, err);
            return null;
          }

          // Retry with exponential backoff
          const backoffMs = Math.pow(2, attempt - 1) * 1000;
          logger.warn(`Retry ${attempt}/${this.maxRetries} for ${file.name} after ${backoffMs}ms`);
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      }

      return null;
    };

    const processQueue = async () => {
      while (queue.length > 0 && activeUploads < this.concurrency) {
        const fileIndex = queue.shift();
        if (fileIndex === undefined) break;

        activeUploads++;
        uploadFile(fileIndex)
          .then(photo => {
            if (photo) {
              uploadedPhotos.push(photo);
            }
          })
          .finally(() => {
            activeUploads--;
            processQueue(); // Process next item in queue
          });
      }
    };

    // Start initial batch
    processQueue();

    // Wait for all uploads to complete
    while (activeUploads > 0 || queue.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (uploadedPhotos.length > 0) {
      logger.info(`Upload complete: ${uploadedPhotos.length}/${files.length} files succeeded`);
      callbacks?.onComplete?.(uploadedPhotos);
    }

    return uploadedPhotos;
  }
}
